import React, { useState } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronRight, Settings } from "lucide-react"
import { useBusinessUtils } from "@/hooks/useBusinessUtils"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useNavigate } from "react-router-dom"
import ComingSoonModal from "../ComingSoonModal"

interface PortfolioSectionHeaderProps {
  title: string;
  portfolioRole?: string | null;
  isExpanded: boolean;
  isSticky?: boolean;
  isMobile?: boolean;
  portfolioId?: string;
  toggleAccordion: () => void;
}

const PortfolioSectionHeader: React.FC<PortfolioSectionHeaderProps> = ({
  title,
  portfolioRole,
  isExpanded,
  isSticky = false,
  isMobile = false,
  portfolioId = '',
  toggleAccordion,
}) => {
  const businessUtils = useBusinessUtils()
  const navigate = useNavigate()
  const [showComingSoonModal, setShowComingSoonModal] = useState(false)
  
  // Don't show settings button for "No Portfolio" section
  const canManagePortfolioSettings = 
    portfolioId !== "no-portfolio" && (
      portfolioRole?.includes('Primary Owner') || 
      portfolioRole?.includes('Owner') || 
      portfolioRole === 'Manager'
    );
  
  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't navigate for "No Portfolio" section
    if (portfolioId !== "no-portfolio") {
      navigate(`/portfolio/${portfolioId}/settings`);
    }
  };

  return (
    <div 
      onClick={toggleAccordion}
      className={cn(
        "flex justify-between items-center py-4 px-4 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors relative",
        isSticky ? "bg-brand-light" : "bg-white",
        isMobile ? "shadow-none" : "shadow-sm"
      )}
    >
      {isMobile ? (
        <>
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col w-full">
              <div className="flex items-center justify-between w-full mb-1">
                <h3 className="text-lg font-semibold">{title}</h3>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 rounded-full p-0 text-gray-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAccordion();
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="flex items-center justify-between w-full">
                {portfolioRole && (
                  <Badge 
                    variant="outline" 
                    className="self-start bg-gray-100 text-gray-700 rounded-md"
                  >
                    {portfolioRole}
                  </Badge>
                )}
                
                {canManagePortfolioSettings && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowComingSoonModal(true)}
                    className="h-6 w-6 text-gray-500 hover:text-primary ml-auto"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center space-x-3">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 rounded-full p-0 text-gray-500"
              onClick={(e) => {
                e.stopPropagation();
                toggleAccordion();
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
            
            <div className="flex-row items-center flex gap-3 w-full">
              <h3 className="text-lg font-semibold">{title}</h3>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {portfolioRole && (
              <Badge 
                variant="outline" 
                className="text-right bg-gray-100 text-gray-700 rounded-md"
              >
                {portfolioRole}
              </Badge>
            )}
            
            {canManagePortfolioSettings && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setShowComingSoonModal(true)}
                      className="h-8 w-8 text-gray-500 hover:text-primary"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Portfolio Settings</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </>
      )}
      <ComingSoonModal
        open={showComingSoonModal}
        onOpenChange={setShowComingSoonModal}
        featureName="Portfolio Settings"
        description="If you want to edit portfolio settings, please contact us."
      />
    </div>
  );
};

export default PortfolioSectionHeader;
